import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Search, Plus, ShieldCheck, FileText, Pencil, X, Upload, ChevronLeft, ChevronRight, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, XCircle, Sparkles, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { useFetch, Spinner } from '../lib/useFetch';
import { sicurezzaApi, companiesApi, sicurezzaImportApi } from '../lib/api';
import { useCrudActions } from '../lib/useCrudActions';
import EntityFormModal from '../components/EntityFormModal';
import { sicurezzaFields } from '../lib/entitySchemas';
import { fmtDate } from '../lib/format';
import { statusBadge } from './FormazionePage';
import { useAuth } from '../context/AuthContext';

/**
 * "Sicurezza sul lavoro" page — aggregated by document type.
 *
 * View modes:
 *   - `list`   → cards, one per canonical document type, showing totali/validi/inScadenza/scaduti
 *   - `detail` → drill-down: aziende con quel documento
 *   - `import` → upload Excel di Alessandro (drag & drop → preview → conferma)
 */
const SicurezzaPage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [view, setView] = useState('list');           // list | detail | import
  const [activeType, setActiveType] = useState(null); // canonical label
  const [search, setSearch] = useState('');

  const { data: docs, loading, reload } = useFetch(() => sicurezzaApi.list(), []);
  const { data: companies } = useFetch(() => companiesApi.list(), []);
  const crud = useCrudActions({ api: sicurezzaApi, label: 'documento sicurezza', onChange: reload });

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || '—';

  const aggregated = useMemo(() => aggregateByType(docs || []), [docs]);

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4" data-testid="sicurezza-page">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#e0e7ff] text-[#023F80] flex items-center justify-center"><ShieldCheck size={20} /></div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Sicurezza sul lavoro — DVR &amp; Adempimenti</h2>
              <p className="text-xs text-slate-500">Vista aggregata per tipologia di documento. Clicca una card per il dettaglio azienda per azienda.</p>
            </div>
          </div>
          {view === 'list' && !isClient && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setView('import')}
                data-testid="import-sicurezza-btn"
                className="ro-hide px-4 py-2 rounded-full bg-white border border-[#023F80] text-[#023F80] hover:bg-[#023F80] hover:text-white text-xs font-bold tracking-wider flex items-center gap-2 transition-colors"
              >
                <Upload size={14} /> IMPORTA EXCEL
              </button>
              <button
                onClick={() => crud.onCreate({ stato: 'valido' })}
                className="ro-hide px-4 py-2 rounded-full bg-[#fef7d4] text-[#8a6608] text-xs font-bold tracking-wider hover:bg-[#E6B800] flex items-center gap-2"
              >
                <Plus size={14} /> NUOVO DOCUMENTO
              </button>
            </div>
          )}
          {view !== 'list' && (
            <button
              onClick={() => { setView('list'); setActiveType(null); }}
              className="px-3 py-2 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-100 flex items-center gap-1"
              data-testid="sicurezza-back-btn"
            >
              <ChevronLeft size={15} /> Indietro
            </button>
          )}
        </div>
      </div>

      {view === 'list' && (
        <AggregatedCards
          aggregated={aggregated}
          onOpen={(label) => { setActiveType(label); setView('detail'); }}
        />
      )}

      {view === 'detail' && activeType && (
        <DetailView
          typeLabel={activeType}
          docs={(docs || []).filter((d) => (d.tipologiaCanonica || canonicalize(d.titolo)) === activeType)}
          companyName={companyName}
          search={search}
          setSearch={setSearch}
          isClient={isClient}
          onEdit={crud.onEdit}
          onDelete={crud.onDelete}
        />
      )}

      {view === 'import' && (
        <ImportPanel onDone={() => { setView('list'); reload(); }} />
      )}

      <EntityFormModal
        open={crud.open}
        onOpenChange={(o) => { if (!o) crud.closeModal(); }}
        title={crud.editing?.id ? 'Modifica documento sicurezza' : 'Nuovo documento sicurezza'}
        fields={sicurezzaFields(companies || [], isClient)}
        initialValues={crud.editing || {}}
        onSubmit={crud.save}
      />
    </div>
  );
};

// ── Aggregation logic ─────────────────────────────────────────────────
const TYPE_RULES = [
  { rx: /legionell/i, label: 'DVR Legionella', years: 2 },
  { rx: /stress/i, label: 'DVR Stress Lavoro-Correlato', years: 3 },
  { rx: /rumore|noise/i, label: 'Valutazione Rumore', years: 4 },
  { rx: /vibraz/i, label: 'Valutazione Vibrazioni', years: 4 },
  { rx: /lavoratrici[-\s]?madr|madri/i, label: 'DVR Lavoratrici Madri', years: null },
  { rx: /chimic/i, label: 'DVR Chimico', years: null },
  { rx: /biologic/i, label: 'DVR Biologico', years: null },
  { rx: /\broa\b|radiaz.*ottic/i, label: 'DVR ROA (Radiazioni Ottiche Artificiali)', years: null },
  { rx: /amb\.?\s*confinati|ambienti.*confinati/i, label: 'DVR Ambienti Confinati', years: null },
  { rx: /\bvdt\b|videoterminal/i, label: 'Rischio VDT', years: null },
  { rx: /alcool|alcol/i, label: 'DVR Alcool', years: null },
  { rx: /\bmmc\b|mov.*manuale.*carich/i, label: 'DVR MMC (Mov. Manuale Carichi)', years: null },
  { rx: /atex/i, label: 'DVR Atex Polveri', years: null },
  { rx: /\bpei\b|piano.*emergenz.*intern/i, label: 'Piano di Emergenza Interno', years: null },
  { rx: /vrincendio|valutaz.*(rischio)?.*incendio|rischio.*incendio/i, label: 'Valutazione Rischio Incendio', years: null },
  { rx: /piano.*emergenz|emergency/i, label: 'Piano di Emergenza', years: null },
  { rx: /piano.*evacuaz/i, label: 'Piano di Evacuazione', years: null },
  { rx: /formazione.*rspp|corso.*rspp/i, label: 'Formazione RSPP', years: null },
  { rx: /^documento.*valutazion.*risch|^valutazion.*rischi\b/i, label: 'DVR Generale', years: null },
  { rx: /^dvr\b/i, label: 'DVR Generale', years: null },
];

const canonicalize = (raw) => {
  const s = (raw || '').trim();
  if (!s) return 'Documento generico';
  // Skip DVR generale for the specific subtypes
  for (const rule of TYPE_RULES) {
    if (rule.label === 'DVR Generale') continue;
    if (rule.rx.test(s)) return rule.label;
  }
  if (/^dvr\b/i.test(s)) return 'DVR Generale';
  return s;
};

const hasAutoScadenza = (label) => {
  const rule = TYPE_RULES.find((r) => r.label === label);
  return !!(rule && rule.years);
};

const aggregateByType = (docs) => {
  const buckets = new Map();
  docs.forEach((d) => {
    const label = d.tipologiaCanonica || canonicalize(d.titolo);
    if (!buckets.has(label)) {
      buckets.set(label, { label, total: 0, valido: 0, in_scadenza: 0, scaduto: 0, no_scadenza: 0 });
    }
    const b = buckets.get(label);
    b.total += 1;
    if (d.scadenza) {
      const stato = liveStatus(d.scadenza) || d.stato;
      if (stato === 'valido' || stato === 'in_scadenza' || stato === 'scaduto') b[stato] += 1;
    } else {
      b.no_scadenza += 1;
    }
  });
  return Array.from(buckets.values()).sort((a, b) => b.total - a.total);
};

const liveStatus = (isoScadenza) => {
  if (!isoScadenza) return null;
  const d = new Date(isoScadenza);
  if (isNaN(d)) return null;
  const days = Math.round((d - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'scaduto';
  if (days <= 30) return 'in_scadenza';
  return 'valido';
};

// ── Aggregated view ───────────────────────────────────────────────────
const AggregatedCards = ({ aggregated, onOpen }) => {
  if (!aggregated.length) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-[#e0e7ff] text-[#023F80] flex items-center justify-center mb-3">
          <ShieldCheck size={28} />
        </div>
        <p className="text-sm text-slate-500">Nessun documento di sicurezza presente.</p>
        <p className="text-xs text-slate-400 mt-1">Importa il file Excel di Alessandro per iniziare.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sicurezza-aggregated-grid">
      {aggregated.map((b) => (
        <button
          key={b.label}
          onClick={() => onOpen(b.label)}
          data-testid={`sicurezza-card-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          className="text-left bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md hover:border-[#023F80] transition-all group"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#e0e7ff] text-[#023F80] flex items-center justify-center group-hover:bg-[#023F80] group-hover:text-white transition-colors">
              <FileText size={18} />
            </div>
            <div className="text-3xl font-extrabold text-[#023F80]">{b.total}</div>
          </div>
          <h3 className="text-sm font-bold text-slate-800 mb-3">{b.label}</h3>
          {hasAutoScadenza(b.label) ? (
            <div className="grid grid-cols-3 gap-1 text-center">
              <StatPill label="Validi" value={b.valido} bg="bg-emerald-50" color="text-emerald-700" />
              <StatPill label="In scadenza" value={b.in_scadenza} bg="bg-amber-50" color="text-amber-700" />
              <StatPill label="Scaduti" value={b.scaduto} bg="bg-red-50" color="text-red-700" />
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 italic pt-2">Senza scadenza automatica — verifica manuale</div>
          )}
        </button>
      ))}
    </div>
  );
};

const StatPill = ({ label, value, bg, color }) => (
  <div className={`${bg} rounded-lg py-1.5`}>
    <div className={`text-lg font-extrabold ${color}`}>{value}</div>
    <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
  </div>
);

// ── Detail view ───────────────────────────────────────────────────────
const DetailView = ({ typeLabel, docs, companyName, search, setSearch, isClient, onEdit, onDelete }) => {
  const showScadenza = hasAutoScadenza(typeLabel);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter((d) => `${companyName(d.companyId)} ${d.responsabile || ''} ${d.note || ''}`.toLowerCase().includes(q));
  }, [docs, search, companyName]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="sicurezza-detail">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#023F80] text-white flex items-center justify-center"><FileText size={16} /></div>
          <div>
            <h3 className="text-base font-extrabold text-slate-800">{typeLabel}</h3>
            <p className="text-xs text-slate-500">{filtered.length} document{filtered.length !== 1 ? 'i' : 'o'} — dettaglio per azienda</p>
          </div>
        </div>
        <div className="relative w-full md:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca azienda..."
            className="pl-9 h-10 border-gray-200"
            data-testid="sicurezza-detail-search"
          />
        </div>
      </div>

      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-gray-500">
              <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Azienda</th>
              <th className="text-left bg-gray-100 px-4 py-3">Data documento</th>
              {showScadenza && <th className="text-left bg-gray-100 px-4 py-3">Data scadenza</th>}
              {showScadenza && <th className="text-left bg-gray-100 px-4 py-3">Stato</th>}
              <th className="text-left bg-gray-100 px-4 py-3">Responsabile</th>
              <th className="bg-gray-100 rounded-r-xl px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50/60">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-slate-400" />
                    <span className="font-semibold text-gray-800">{companyName(s.companyId)}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-700">{fmtDate(s.dataRilascio)}</td>
                {showScadenza && <td className="px-4 py-3 text-gray-700">{fmtDate(s.scadenza) || '—'}</td>}
                {showScadenza && <td className="px-4 py-3">{statusBadge(liveStatus(s.scadenza) || s.stato)}</td>}
                <td className="px-4 py-3 text-gray-700">{s.responsabile || '—'}</td>
                <td className="px-4 py-3">
                  {!isClient && (
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => onEdit(s)} title="Modifica" className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><Pencil size={13} className="text-gray-600" /></button>
                      <button onClick={() => onDelete(s)} title="Elimina" className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><X size={14} className="text-red-600" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-10 text-center text-sm text-gray-400">Nessun documento in questa tipologia</div>}
      </div>
    </div>
  );
};

// ── Import panel ──────────────────────────────────────────────────────
const ImportPanel = ({ onDone }) => {
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);

  const onFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Serve un file .xlsx');
      return;
    }
    setFile(f);
    setSession(null);
  };

  const runPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await sicurezzaImportApi.preview(fd);
      setSession(res.data);
      toast.success(`Anteprima: ${res.data.stats.letto} righe lette`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore lettura file');
    } finally {
      setLoading(false);
    }
  };

  const runConfirm = async () => {
    if (!session?.sessionId) return;
    if (!window.confirm(`Confermi l'importazione? ${session.stats.nuovi} nuovi, ${session.stats.aggiornati} aggiornamenti.`)) return;
    setLoading(true);
    try {
      const res = await sicurezzaImportApi.confirm(session.sessionId);
      toast.success(`Import completato: ${res.data.created} creati, ${res.data.updated} aggiornati`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore importazione');
    } finally {
      setLoading(false);
    }
  };

  const stats = session?.stats;

  return (
    <div className="space-y-4">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#023F80] to-[#022F60] text-white p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#FFCB05] text-[#023F80] flex items-center justify-center"><FileSpreadsheet size={22} /></div>
          <div>
            <h2 className="text-xl font-extrabold">Import documenti sicurezza</h2>
            <p className="text-white/70 text-sm">Carica il file Excel: colonne attese Azienda, Tipologia, Data documento (+ Note e Responsabile opzionali).</p>
          </div>
        </div>
      </div>

      {/* Upload step */}
      {!session && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div
            className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-[#023F80] cursor-pointer bg-slate-50/40"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
          >
            <FileSpreadsheet size={36} className="mx-auto text-[#023F80] mb-2" />
            <div className="text-sm font-semibold text-slate-700">{file ? file.name : 'Clicca o trascina qui il file .xlsx'}</div>
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="sicurezza-import-file-input" />
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={runPreview}
              disabled={!file || loading}
              data-testid="sicurezza-import-preview-btn"
              className="px-5 py-2 rounded-lg bg-[#023F80] hover:bg-[#022F60] text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} className="text-[#FFCB05]" />}
              Analizza file
            </button>
          </div>
        </div>
      )}

      {/* Preview */}
      {session && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <ImpStat label="Righe lette" value={stats.letto} color="#023F80" bg="#e0e7ff" />
            <ImpStat label="Nuovi" value={stats.nuovi} color="#0e7c3a" bg="#dcfce7" />
            <ImpStat label="Aggiornati" value={stats.aggiornati} color="#8a6608" bg="#fef7d4" />
            <ImpStat label="Invariati" value={stats.invariati} color="#525252" bg="#f4f4f5" />
            <ImpStat label="Senza azienda" value={stats.senza_azienda} color="#b91c1c" bg="#fee2e2" />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="border-b border-slate-100 px-4 py-2 flex flex-wrap gap-2 justify-end">
              <button
                onClick={runConfirm}
                disabled={loading || stats.nuovi + stats.aggiornati === 0}
                data-testid="sicurezza-import-confirm-btn"
                className="px-4 py-1.5 rounded-lg bg-[#023F80] hover:bg-[#022F60] text-white text-xs font-bold flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} className="text-[#FFCB05]" />}
                Conferma importazione
              </button>
            </div>
            <div className="p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="text-left bg-gray-100 rounded-l-xl px-3 py-2.5">Riga</th>
                    <th className="text-left bg-gray-100 px-3 py-2.5">Azione</th>
                    <th className="text-left bg-gray-100 px-3 py-2.5">Azienda</th>
                    <th className="text-left bg-gray-100 px-3 py-2.5">Tipologia</th>
                    <th className="text-left bg-gray-100 px-3 py-2.5">Data</th>
                    <th className="text-left bg-gray-100 px-3 py-2.5">Scadenza auto</th>
                    <th className="text-left bg-gray-100 rounded-r-xl px-3 py-2.5">Anomalie</th>
                  </tr>
                </thead>
                <tbody>
                  {(session.sample || []).map((it) => (
                    <tr key={`${it.row}-${it.tipologiaCanonica}`} className="border-b border-gray-50">
                      <td className="px-3 py-2 text-slate-600">{it.row}</td>
                      <td className="px-3 py-2"><ActionPill action={it.action} /></td>
                      <td className="px-3 py-2 text-slate-800">
                        {it.companyName || <span className="text-red-600 flex items-center gap-1"><AlertTriangle size={12} /> {it.azienda}</span>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-800">{it.tipologiaCanonica}</div>
                        {it.tipologiaCanonica !== it.tipologia && <div className="text-[10px] text-slate-500">→ da: {it.tipologia}</div>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{it.dataRilascio || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {it.scadenza ? <span className="text-emerald-700">{it.scadenza} <span className="text-[10px] text-slate-500">(+{it.autoScadenzaYears}a)</span></span> : <span className="text-slate-400 text-[11px] italic">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {(it.anomalies || []).map((a, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 mr-1 font-semibold">
                            <XCircle size={11} /> {a.type}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ImpStat = ({ label, value, color, bg }) => (
  <div className="rounded-xl p-3" style={{ background: bg }}>
    <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-600">{label}</div>
    <div className="text-2xl font-extrabold mt-1" style={{ color }}>{value ?? 0}</div>
  </div>
);

const ActionPill = ({ action }) => {
  const map = {
    create: { label: 'Nuovo', bg: 'bg-emerald-100', color: 'text-emerald-800' },
    update: { label: 'Aggiorna', bg: 'bg-amber-100', color: 'text-amber-800' },
    unchanged: { label: 'Invariato', bg: 'bg-slate-100', color: 'text-slate-600' },
    skip: { label: 'Saltato', bg: 'bg-red-50', color: 'text-red-700' },
  };
  const m = map[action] || map.unchanged;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${m.bg} ${m.color}`}>{m.label}</span>;
};

export default SicurezzaPage;
